/**
 * shopifyPush — Depo'dan online mağazaya fiyat & stok gönderimi.
 *
 * Eşleştirme SKU üzerinden yapılır (shopifySync ile aynı sözleşme).
 * Proxy yalnızca iki mutation'a izin verir (PushVariantPrice,
 * PushInventory) ve admin oturumu zorunludur — mağazada başka hiçbir
 * değişiklik bu kanaldan yapılamaz.
 */
import { shopifyGraphQL } from '@/lib/shopifyClient';
import { kgToShopifyWeight, toShopifyUnitPrice } from '@/lib/grundpreis';

/**
 * Shop-Felder einer Variante (Bulk-Update-Form): SKU + Gewicht unter
 * inventoryItem, Grundpreis als unitPriceMeasurement + showUnitPrice.
 * Gewicht & Grundpreis werden nach Shopify gespiegelt — b2b_preis und
 * EWK-Felder NIE. Leere Teile werden weggelassen, damit nichts überschrieben
 * wird, was lokal nicht gesetzt ist.
 */
function variantShopFields(v) {
  const weight = kgToShopifyWeight(v?.gewicht_kg);
  const inventoryItem = {
    ...(v?.sku && { sku: v.sku }),
    ...(weight && { measurement: { weight } }),
  };
  return {
    ...(Object.keys(inventoryItem).length > 0 && { inventoryItem }),
    ...(toShopifyUnitPrice(v) || {}),
  };
}

const FIND_VARIANT = `
  query FindVariantBySku($query: String!) {
    productVariants(first: 1, query: $query) {
      nodes {
        id
        sku
        price
        product { id title }
        inventoryItem { id }
      }
    }
  }
`;

const GET_LOCATIONS = `
  query GetLocations {
    locations(first: 1, query: "active:true") {
      nodes { id name }
    }
  }
`;

const PUSH_PRICE = `
  mutation PushVariantPrice($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
    productVariantsBulkUpdate(productId: $productId, variants: $variants) {
      userErrors { field message }
    }
  }
`;

const PUSH_INVENTORY = `
  mutation PushInventory($input: InventorySetOnHandQuantitiesInput!) {
    inventorySetOnHandQuantities(input: $input) {
      userErrors { field message }
    }
  }
`;

let cachedLocationId = null;

async function getPrimaryLocationId() {
  if (cachedLocationId) return cachedLocationId;
  const data = await shopifyGraphQL(GET_LOCATIONS);
  const loc = data?.locations?.nodes?.[0];
  if (!loc) throw new Error('Keine aktive Shopify-Location gefunden.');
  cachedLocationId = loc.id;
  return cachedLocationId;
}

async function findVariantBySku(sku) {
  const data = await shopifyGraphQL(FIND_VARIANT, { query: `sku:${JSON.stringify(sku)}` });
  return data?.productVariants?.nodes?.[0] || null;
}

/**
 * Tek bir SKU için fiyat + stok push'u.
 * @returns {{sku, ok, message}}
 */
async function pushSku({ sku, price, stock }) {
  const variant = await findVariantBySku(sku);
  if (!variant) return { sku, ok: false, message: 'SKU im Shop nicht gefunden' };

  // 1) Fiyat
  if (price != null && Number(price) > 0) {
    const res = await shopifyGraphQL(PUSH_PRICE, {
      productId: variant.product.id,
      variants: [{ id: variant.id, price: String(Number(price).toFixed(2)) }],
    });
    const errs = res?.productVariantsBulkUpdate?.userErrors || [];
    if (errs.length > 0) return { sku, ok: false, message: errs[0].message };
  }

  // 2) Stok (on-hand set — mutlak değer)
  if (stock != null && Number.isFinite(Number(stock))) {
    const locationId = await getPrimaryLocationId();
    const res = await shopifyGraphQL(PUSH_INVENTORY, {
      input: {
        reason: 'correction',
        setQuantities: [{
          inventoryItemId: variant.inventoryItem.id,
          locationId,
          quantity: Math.max(0, Math.round(Number(stock))),
        }],
      },
    });
    const errs = res?.inventorySetOnHandQuantities?.userErrors || [];
    if (errs.length > 0) return { sku, ok: false, message: errs[0].message };
  }

  return { sku, ok: true, message: 'OK' };
}

/**
 * Bir ürünü (varyantlarıyla) mağazaya gönderir.
 * - Varyantlı ürün: SKU'su olan her varyant için fiyat=ürün fiyatı,
 *   stok=varyant stoğu gönderilir.
 * - Varyantsız: master SKU ile fiyat + toplam stok.
 * @returns {Array<{sku, ok, message}>}
 */
export async function pushProductToShop(product) {
  // PIM P4: delegiert an die Preisebenen-bewusste Variante.
  // WICHTIG: Es geht NUR noch online_preis raus — der B2B-Preis
  // (product.price/preis) verlässt FieldConnect nie mehr.
  return pushOperationalData(product);
}

/* ════════════════════════════════════════════════════════════════
 * PIM P4 — "Operation rauf": Verfügbar-Bestand + Online-Preis.
 * B2B-Preise verlassen FieldConnect NIEMALS.
 * ════════════════════════════════════════════════════════════════ */
import { base44 } from '@/api/base44Client';

const PUBLISH_PRODUCT = `
  mutation PublishProduct($input: ProductSetInput!) {
    productSet(input: $input) {
      product {
        id
        variants(first: 100) { nodes { id sku inventoryItem { id } } }
      }
      userErrors { field message }
    }
  }
`;

/** Verfügbar = Bestand − Reserviert (Variante erbt vom Produkt) */
function verfuegbar(product, variant) {
  if (variant?.bestand != null) return Math.max(0, Number(variant.bestand) || 0);
  const stock = Number(product.stock) || 0;
  const reserved = Number(product.reserved_stock) || 0;
  return Math.max(0, stock - reserved);
}

/**
 * Operationsdaten eines Produkts pushen: pro aktiver Variante mit SKU
 * Online-Preis (falls gesetzt) + Verfügbar-Bestand. Idempotent.
 */
export async function pushOperationalData(product, variants = null) {
  if (variants == null) {
    try {
      variants = await base44.entities.ProductVariant.filter({ product_id: product.id }, 'position');
    } catch { variants = []; }
  }
  const active = (variants || []).filter((v) => v.aktiv !== false && v.sku);

  const targets = active.length > 0
    ? active.map((v) => ({ sku: v.sku, price: v.online_preis, stock: verfuegbar(product, v) }))
    : product.sku
      ? [{ sku: product.sku, price: null, stock: verfuegbar(product, null) }]
      : [];

  if (targets.length === 0) {
    return [{ sku: '—', ok: false, message: 'Keine Variante mit SKU — Push nicht möglich' }];
  }

  const results = [];
  for (const t of targets) {
    try {
      results.push(await pushSku(t));
    } catch (err) {
      results.push({ sku: t.sku, ok: false, message: err.message });
    }
  }
  return results;
}

/**
 * Bestands-Push für mehrere Produkte im Hintergrund (fire-and-forget
 * nach Lagerbewegungen). Fehler werden nur geloggt — Lagerablauf
 * darf nie an einem Shop-Push scheitern.
 */
export async function pushStockForProducts(productIds = []) {
  for (const id of [...new Set(productIds)].filter(Boolean)) {
    try {
      const product = await base44.entities.Product.get(id);
      if (!product?.channel_shopify) continue; // nur veröffentlichte Produkte
      await pushOperationalData(product);
    } catch (err) {
      console.warn('[shopifyPush] Hintergrund-Push fehlgeschlagen:', id, err.message);
    }
  }
}

/**
 * FC-Produkt in Shopify VERÖFFENTLICHEN (productSet).
 * Nach Erfolg: shopify_product_id + channel_shopify am Produkt,
 * Varianten-IDs gespiegelt, danach Bestand gepusht.
 */
export async function publishProductToShopify(product, variants = []) {
  const active = (variants || []).filter((v) => v.aktiv !== false);
  if (active.length === 0) throw new Error('Produkt hat keine Varianten.');
  if (!active.some((v) => v.sku)) throw new Error('Mindestens eine Variante braucht eine SKU.');
  if (!active.some((v) => v.online_preis != null)) {
    throw new Error('Online-Preis fehlt — bitte in der Produktkarte (Varianten) setzen.');
  }

  // Optionen aus den Varianten ableiten (z. B. Größe/Farbe)
  const optionNames = [...new Set(active.flatMap((v) => Object.keys(v.options || {})))];
  const input = {
    title: product.name,
    descriptionHtml: product.description_html || product.beschreibung || product.description || '',
    status: 'ACTIVE',
    ...(optionNames.length > 0 && {
      productOptions: optionNames.map((name) => ({
        name,
        values: [...new Set(active.map((v) => v.options?.[name]).filter(Boolean))]
          .map((value) => ({ name: value })),
      })),
    }),
    variants: active.map((v) => {
      const variant = {
        sku: v.sku || undefined,
        price: v.online_preis != null ? String(Number(v.online_preis).toFixed(2)) : undefined,
        compareAtPrice: v.compare_at_preis != null ? String(Number(v.compare_at_preis).toFixed(2)) : undefined,
        ...(optionNames.length > 0 && {
          optionValues: optionNames.map((name) => ({
            optionName: name,
            name: v.options?.[name] || 'Standard',
          })),
        }),
      };
      // Gewicht & Grundpreis mitgeben (ProductVariantSetInput nimmt die SKU
      // top-level, daher inventoryItem hier nur mit measurement). EWK/B2B nie.
      const shop = variantShopFields(v);
      if (shop.inventoryItem?.measurement) {
        variant.inventoryItem = { measurement: shop.inventoryItem.measurement };
      }
      if (shop.unitPriceMeasurement) {
        variant.unitPriceMeasurement = shop.unitPriceMeasurement;
        variant.showUnitPrice = shop.showUnitPrice;
      }
      return variant;
    }),
  };

  const res = await shopifyGraphQL(PUBLISH_PRODUCT, { input });
  const errs = res?.productSet?.userErrors || [];
  if (errs.length > 0) throw new Error(errs.map((e) => e.message).join('; '));
  const created = res?.productSet?.product;
  if (!created?.id) throw new Error('Shopify hat kein Produkt zurückgegeben.');

  // Lokale Spiegelung: Produkt + Varianten-IDs
  await base44.entities.Product.update(product.id, {
    shopify_product_id: created.id,
    channel_shopify: true,
    content_synced_at: new Date().toISOString(),
  });
  const bySku = new Map((created.variants?.nodes || []).map((n) => [n.sku, n]));
  for (const v of active) {
    const node = v.sku ? bySku.get(v.sku) : null;
    if (node) {
      await base44.entities.ProductVariant.update(v.id, {
        shopify_variant_id: node.id,
        shopify_inventory_item_id: node.inventoryItem?.id || null,
      });
    }
  }

  // Erstbestand pushen
  const pushResults = await pushOperationalData({ ...product, channel_shopify: true }, active);
  return { shopifyProductId: created.id, pushResults };
}

/* ════════════════════════════════════════════════════════════════
 * PIM P3b — Bearbeitung aus der Produktkarte nach Shopify spiegeln:
 * Titel (productUpdate) und Varianten-SKU/Preise (bulk update).
 * B2B-Preis wird NIE übertragen.
 * ════════════════════════════════════════════════════════════════ */
const UPDATE_TITLE = `
  mutation UpdateProductTitle($input: ProductInput!) {
    productUpdate(input: $input) {
      product { id title }
      userErrors { field message }
    }
  }
`;
const PUSH_DETAILS = `
  mutation PushVariantDetails($productId: ID!, $variants: [ProductVariantsBulkInput!]!) {
    productVariantsBulkUpdate(productId: $productId, variants: $variants) {
      productVariants { id sku price inventoryItem { measurement { weight { value unit } } } }
      userErrors { field message }
    }
  }
`;

/** Produkttitel nach Shopify (nur wenn verknüpft) */
export async function pushProductTitle(product, title) {
  if (!product?.shopify_product_id) return { ok: false, message: 'nicht mit Shopify verknüpft' };
  const res = await shopifyGraphQL(UPDATE_TITLE, { input: { id: product.shopify_product_id, title } });
  const errs = res?.productUpdate?.userErrors || [];
  if (errs.length > 0) return { ok: false, message: errs.map((e) => e.message).join('; ') };
  return { ok: true };
}

/**
 * Varianten-Details (SKU, Online-/Streichpreis, Gewicht, Grundpreis) nach
 * Shopify. Nur Varianten mit shopify_variant_id; b2b_preis wird NIE übertragen.
 */
export async function pushVariantDetails(product, variants) {
  if (!product?.shopify_product_id) return { ok: false, message: 'nicht mit Shopify verknüpft' };
  const targets = (variants || []).filter((v) => v.shopify_variant_id && v.aktiv !== false);
  if (targets.length === 0) return { ok: false, message: 'keine verknüpften Varianten' };
  const res = await shopifyGraphQL(PUSH_DETAILS, {
    productId: product.shopify_product_id,
    variants: targets.map((v) => {
      // Streichpreis nur senden, wenn gesetzt ODER bewusst geleert (im Patch) —
      // sonst würde ein Gewichts-/Grundpreis-Save den Shopify-Streichpreis löschen.
      const compareCleared = !!v._patch && 'compare_at_preis' in v._patch;
      return {
        id: v.shopify_variant_id,
        ...(v.online_preis != null && { price: String(Number(v.online_preis).toFixed(2)) }),
        ...(v.compare_at_preis != null
          ? { compareAtPrice: String(Number(v.compare_at_preis).toFixed(2)) }
          : compareCleared ? { compareAtPrice: null } : {}),
        ...variantShopFields(v),
      };
    }),
  });
  const errs = res?.productVariantsBulkUpdate?.userErrors || [];
  if (errs.length > 0) return { ok: false, message: errs.map((e) => e.message).join('; ') };
  return { ok: true, count: targets.length };
}
